import React, { ReactElement, useEffect, useState } from 'react';
import { Box, Button, Dialog, Typography } from '@mui/material';
import {
  gray40,
  gray80,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldMd } from '../../../constants/location';
import { SUB_PATTERN_ID_BASE } from '../../../constants/enum';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import type { SubPatternType } from '../../../types/subPattern';
import SubPatternReOcrSelect from '../parts/SubPatternReOcrSelect';

type Props = {
  open: boolean;
  onClose: () => void;
  /** 選ばれた子パターンで読み直す。子パターンを使わない場合は undefined */
  onConfirm: (subPatternId?: string) => void;
  usesCredit?: boolean;
  /**
   * 選択肢に出す子パターン（#2967）。空なら選択欄を出さない。
   * 子パターンを使うパターンでは、どの子パターンで読み直すかをここで選ばせる
   */
  subPatterns?: SubPatternType[];
  /** 現在適用されている子パターンID。初期選択に使う */
  currentSubPatternId?: string | null;
};

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      maxWidth: '480px',
      '@media screen and (max-width: 960px)': {
        padding: '32px 16px',
      },
    },
  },
  message: {
    whiteSpace: 'pre-line',
    lineHeight: 1.8,
  },
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '24px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  confirmButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  confirmButtonText: {
    ...boldMd,
    color: 'white',
  },
};

const OcrCreditConsumeConfirmModal = ({
  open,
  onClose,
  onConfirm,
  usesCredit = false,
  subPatterns = [],
  currentSubPatternId,
}: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();

  // 選択中の子パターン。'base' は子パターンを使わず読取パターンの設定で読む
  const [selectedLayout, setSelectedLayout] = useState<string>(SUB_PATTERN_ID_BASE);

  // 開くたびに現在の状態へ戻す。前回の選択が残っていると、
  // 「今どの子パターンで読み直そうとしているか」を取り違える
  useEffect(() => {
    if (!open) return;
    setSelectedLayout(currentSubPatternId ?? SUB_PATTERN_ID_BASE);
  }, [open, currentSubPatternId]);

  const message = usesCredit
    ? 'この操作はOCR枚数を消費します。今月の無料利用枠を使い切ったため、OCRクレジットから消費して続行します。'
    : 'この操作はOCR枚数を消費します。';

  const showLayoutSelect = subPatterns.length > 0;

  return (
    <Dialog open={open} onClose={onClose} sx={[styles.dialog, proxyDialogSx]}>
      <Typography sx={styles.message}>{message}</Typography>

      {/*
        子パターンの選択（#2967）。子パターンを持つパターンでのみ出す。
        判定が誤っていた場合に、正しい子パターンを選んで読み直せるようにする。
      */}
      {showLayoutSelect && (
        <SubPatternReOcrSelect
          subPatterns={subPatterns}
          value={selectedLayout}
          onChange={setSelectedLayout}
        />
      )}

      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onClose}>
          <Typography sx={styles.cancelButtonText}>キャンセル</Typography>
        </Button>
        <Button
          sx={styles.confirmButton}
          onClick={(): void => onConfirm(showLayoutSelect ? selectedLayout : undefined)}
        >
          <Typography sx={styles.confirmButtonText}>実行する</Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default OcrCreditConsumeConfirmModal;
